import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CharacterCard,
  characterFromArchetype,
  characterFromDraft,
  pillarsError,
  type CharacterDraft,
} from '@/components/triggers/CharacterCard';
import { Field } from '@/components/triggers/Field';
import {
  DEFAULT_ARCHETYPE,
  DEFAULT_AVATAR_REFS,
  DEFAULT_LANGUAGE,
  DEFAULT_PLATFORM,
  PLATFORM_OPTIONS,
  RESERVED_TRAIT_KEYS,
} from '@/constants/triggers';
import { api } from '@/lib/api';
import type { Platform } from '@/types/common';
import type { ArchetypeSlug } from '@/types/persona';

export function TriggerPersonaPage({ initialArchetype }: { initialArchetype?: ArchetypeSlug } = {}) {
  const navigate = useNavigate();
  const archetypesQ = useQuery({
    queryKey: ['archetypes'],
    queryFn: () => api.archetypes(),
  });

  const [archetype, setArchetype] = useState<ArchetypeSlug>(initialArchetype ?? DEFAULT_ARCHETYPE);
  const [platform, setPlatform] = useState<Platform>(DEFAULT_PLATFORM);

  const [character, setCharacter] = useState<CharacterDraft>(characterFromArchetype(undefined));
  const [extras, setExtras] = useState<{ key: string; value: string }[]>([]);

  const selected = useMemo(
    () => archetypesQ.data?.find((a) => a.slug === archetype),
    [archetypesQ.data, archetype],
  );

  // The API lists only the active niche's archetypes. If the default (or a
  // deep-linked slug) isn't offered, snap to the first one that is.
  useEffect(() => {
    const offered = archetypesQ.data;
    const first = offered?.[0];
    if (!first || offered.some((a) => a.slug === archetype)) return;
    setArchetype(first.slug);
  }, [archetypesQ.data, archetype]);

  // Claude drafts the character fields for the selected archetype — on first
  // load (default archetype) and on every archetype change. Cached per
  // archetype for the session; "Redraft" asks again.
  const draftQ = useQuery({
    queryKey: ['character-draft', archetype],
    queryFn: () => api.draftCharacter(archetype),
    enabled: !!selected,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!selected) return;
    setCharacter(characterFromArchetype(selected));
  }, [selected]);

  // Declared after the archetype effect so a cached draft wins on switch-back.
  useEffect(() => {
    if (selected && draftQ.data?.archetype === selected.slug) {
      setCharacter(characterFromDraft(draftQ.data));
    }
  }, [selected, draftQ.data]);
  const drafting = !!selected && draftQ.isFetching;

  const buildSeedTraits = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    // The archetype's one-liner and content beats are used as-is (no
    // override UI); only the visual identity can replace the seed's.
    if (selected) {
      const visual = character.visualIdentity.trim();
      if (visual && visual !== selected.visual_archetype) {
        out.visual_archetype = visual;
      }
    }
    // "Define the character" fields: sent whenever set (prefilled values
    // included — what the form shows is what the persona gets).
    const c = character;
    if (c.audience.trim()) out.audience = c.audience.trim();
    if (c.displayName.trim()) out.display_name = c.displayName.trim();
    if (c.bio.trim()) out.bio = c.bio.trim();
    if (c.tone.trim()) out.tone = c.tone.trim();
    if (c.pillars.every((p) => p.name.trim())) {
      out.pillars = c.pillars.map((p) => ({
        name: p.name.trim(),
        description: (p.description ?? '').trim(),
      }));
    }
    const claims = c.extraClaims
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (claims.length) out.claims_boundaries = claims;
    for (const { key, value } of extras) {
      const k = key.trim();
      if (!k || RESERVED_TRAIT_KEYS.has(k) || k in out) continue; // form fields win
      out[k] = value;
    }
    return out;
  };

  const trigger = useMutation({
    mutationFn: () =>
      api.triggerPersona({
        archetype,
        target_platform: platform,
        // Fixed for this deployment — not operator choices.
        language: DEFAULT_LANGUAGE,
        n_avatar_refs: DEFAULT_AVATAR_REFS,
        seed: null,
        seed_traits: buildSeedTraits(),
      }),
    onSuccess: (resp) => {
      toast.success(`Started persona-gen ${resp.entity_id}`, {
        description: `workflow: ${resp.workflow_id}`,
      });
      void navigate({ to: '/runs/$workflowId', params: { workflowId: resp.workflow_id } });
    },
    onError: (err) => {
      toast.error('Trigger failed', { description: String(err) });
    },
  });

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Trigger persona generation</h1>
        <p className="text-sm text-muted-foreground">
          Pick an archetype and Claude drafts the character below. Review it, edit anything, then
          start generation: ~1–2 minutes for the voice sheet and hero image (English, one hero
          image). Lands in /personas on completion.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Inputs</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Archetype">
            {archetypesQ.isLoading ? (
              <Skeleton className="h-9" />
            ) : (
              <Select
                value={archetype}
                onChange={(e) => setArchetype(e.target.value as ArchetypeSlug)}
              >
                {(archetypesQ.data ?? []).map((a) => (
                  <option key={a.slug} value={a.slug}>
                    {a.label} — {a.one_liner}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Platform">
            <Select value={platform} onChange={(e) => setPlatform(e.target.value as Platform)}>
              {PLATFORM_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      <CharacterCard
        archetype={selected}
        value={character}
        onChange={setCharacter}
        drafting={drafting}
        draftError={draftQ.isError && !drafting ? String(draftQ.error) : null}
        onRedraft={() => void draftQ.refetch()}
      />

      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-3">
          <div className="space-y-1">
            <CardTitle>Custom traits</CardTitle>
            <CardDescription>
              Free-form key/value overrides forwarded to the prompt as a JSON block. Use for
              flavor like <code>region=texas</code> or <code>vibe=loud_motivational</code>.
              <span className="ml-1">
                <code>niche</code> and <code>sport</code> are reserved (set per deployment).
              </span>
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setExtras([...extras, { key: '', value: '' }])}
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {extras.length === 0 && (
            <p className="text-xs text-muted-foreground">No custom traits.</p>
          )}
          {extras.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={t.key}
                onChange={(e) =>
                  setExtras(
                    extras.map((x, j) => (i === j ? { ...x, key: e.target.value } : x)),
                  )
                }
                placeholder="key"
                className="max-w-[200px]"
              />
              <Input
                value={t.value}
                onChange={(e) =>
                  setExtras(
                    extras.map((x, j) => (i === j ? { ...x, value: e.target.value } : x)),
                  )
                }
                placeholder="value"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setExtras(extras.filter((_, j) => i !== j))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {Object.keys(buildSeedTraits()).length === 0
            ? 'No overrides — using archetype defaults verbatim.'
            : `${Object.keys(buildSeedTraits()).length} override(s) will be sent.`}
        </p>
        <Button
          onClick={() => trigger.mutate()}
          disabled={trigger.isPending || drafting || !!pillarsError(character.pillars)}
        >
          {trigger.isPending ? 'Triggering…' : 'Start persona generation'}
        </Button>
      </div>
    </div>
  );
}
